/**
 * CCTV 핑 점검 — 통합서버에서 현장 DB 의 주차장 CCTV 전부를 핑해 응답 없는 CCTV 만 층별로 보인다.
 * 지금은 맵으로 등록한 지하주차장 CCTV 뿐이다(옥외·기타 IP 장비는 아직 없다).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import { QaPingCctv, SiteParkingQaService } from '../../services/SiteParkingQaService';

interface Props {
  projectId: number;
  ip: string;
  /** CCTV 를 누르면 상세(연결 주차면·카메라 판정)를 연다. */
  onOpen: (name: string) => void;
  /** 점검 결과가 바뀔 때마다 — 맵이 문제 있는 것만 칠하는 데 쓴다. */
  onResult?: (result: QaPingCctv[] | null) => void;
}

const QaPingPanel: React.FC<Props> = ({ projectId, ip, onOpen, onResult }) => {
  const [cctvs, setCctvs] = useState<QaPingCctv[] | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setCctvs(await SiteParkingQaService.ping(projectId, ip));
      setCheckedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'CCTV 핑 점검 실패');
    } finally {
      setBusy(false);
    }
  }, [projectId, ip]);

  useEffect(() => {
    check();
  }, [check]);
  useEffect(() => {
    onResult?.(cctvs);
  }, [cctvs, onResult]);

  const down = useMemo(() => (cctvs ?? []).filter((c) => !c.alive), [cctvs]);
  const byLevel = useMemo(() => {
    const m = new Map<string, QaPingCctv[]>();
    down.forEach((c) => {
      const k = c.level || '층 미지정';
      m.set(k, [...(m.get(k) ?? []), c]);
    });
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b, 'ko'));
  }, [down]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }} data-testid="qa-ping">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 'auto' }}>
          CCTV 핑 점검 · 지하주차장
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          onClick={check}
          disabled={busy}
          sx={{ minHeight: 44 }}
        >
          {busy ? '점검 중…' : '다시 점검'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p">
        통합서버에서 CCTV 마다 핑하고, 응답이 없으면 천천히 한 번 더 확인합니다. 그래도 응답하지
        않은 CCTV 만 아래에 보입니다.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      {cctvs && (
        <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }} data-testid="qa-ping-summary">
          전체 {cctvs.length}대 중{' '}
          <Box component="span" sx={{ color: down.length ? 'error.main' : 'success.main' }}>
            응답 없음 {down.length}대
          </Box>
          {checkedAt && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · {checkedAt.toLocaleTimeString()} 점검
            </Typography>
          )}
        </Typography>
      )}
      {cctvs && down.length === 0 && !error && (
        <Alert severity="success" sx={{ mt: 1 }}>
          모든 CCTV 가 응답합니다.
        </Alert>
      )}
      {byLevel.map(([level, list]) => (
        <Box key={level} sx={{ mt: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            {level} · {list.length}
          </Typography>
          <Divider />
          {list.map((c) => (
            <Box
              key={c.name}
              component="button"
              type="button"
              onClick={() => onOpen(c.name)}
              data-testid="qa-ping-down"
              sx={{
                display: 'flex',
                width: '100%',
                minHeight: 44,
                alignItems: 'center',
                gap: 1.5,
                px: 0.5,
                border: 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'transparent',
                color: 'text.primary',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                {c.name}
              </Typography>
              <Typography variant="body2" color={c.ip ? 'error.main' : 'text.disabled'} sx={{ fontFamily: 'monospace' }}>
                {c.ip || 'IP 없음'}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Paper>
  );
};

export default QaPingPanel;
